import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Clock, ArrowRight, AlertTriangle } from "lucide-react";

/**
 * Sticky banner at the top of authenticated pages showing trial state:
 * - trialing + >3 days: subtle orange "X days left of Pro trial"
 * - trialing + ≤3 days: red urgency banner
 * - trial ended: red "Trial ended — pick a plan" banner
 * - otherwise: nothing (paid, cancelled, or free-after-trial)
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const trial = user?.trial;
  if (!trial) return null;

  // Only show for owners on trialing or just-expired trial.
  if (!trial.in_trial && !trial.trial_ended) return null;

  if (trial.trial_ended) {
    return (
      <div
        data-testid="trial-banner-ended"
        className="sticky top-0 z-40 bg-[var(--peak-accent)] text-white flex items-center justify-between px-6 py-2.5 border-b-2 border-black"
      >
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
          <AlertTriangle className="w-4 h-4" />
          <span>Your Pro trial has ended — you&apos;re on the Free plan until you pick a paid plan.</span>
        </div>
        <Link
          to="/settings/billing"
          className="bg-black text-white px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-[var(--peak-accent)] transition-colors flex items-center gap-2"
        >
          Pick a plan <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const isUrgent = trial.days_left != null && trial.days_left <= 3;
  const bg = isUrgent ? "bg-[var(--peak-accent)]" : "bg-[var(--peak-ink)]";
  const label = trial.days_left === 1 ? "1 day left" : `${trial.days_left} days left`;

  return (
    <div
      data-testid="trial-banner"
      className={`sticky top-0 z-40 ${bg} text-white flex items-center justify-between px-6 py-2 border-b-2 border-black`}
    >
      <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
        {isUrgent ? <AlertTriangle className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
        <span className="flex items-center gap-2">
          {isUrgent ? "Trial ending soon" : "Pro Trial"}
          <span className="font-mono normal-case tracking-normal opacity-90"><Clock className="w-3 h-3 inline mb-0.5 mr-1" />{label}</span>
        </span>
      </div>
      <Link
        to="/settings/billing"
        data-testid="trial-banner-upgrade"
        className="text-xs font-bold uppercase tracking-wider hover:underline flex items-center gap-1"
      >
        Upgrade <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
